/**
 * StringCell Component
 * 
 * Renders string values with:
 * - Truncation for long text (>40 chars)
 * - Click to expand for full content
 * - Visual indicator (expand icon) for truncated values
 * - Special display for null/undefined/empty values
 * - SECURITY: Masks sensitive fields (sessionToken, password, etc.)
 */

import { useState } from 'react';
import { CellRendererProps } from '../../../../../types/AppPanel';

const MAX_LENGTH = 60;

/**
 * Patterns to identify sensitive fields that should be masked.
 * Matches field names containing these patterns (case-insensitive).
 */
const SENSITIVE_PATTERNS = [
  'sessiontoken',
  'password',
  'authdata',
  'accesstoken',
  'refreshtoken',
  'apikey',
  'secretkey',
  'privatekey',
  'token',
  '_hashed_password',
  'bcryptpassword',
  'secret',
  'credential',
];

/**
 * Check if a field name indicates sensitive data
 */
function isSensitiveField(fieldName: string): boolean {
  const lower = fieldName.toLowerCase();
  return SENSITIVE_PATTERNS.some(pattern => lower.includes(pattern));
}

export default function StringCell({ 
  value, 
  field,
  onExpand 
}: CellRendererProps<string | null | undefined>) {
  // State for revealing sensitive values (resets when cell rerenders)
  const [isRevealed, setIsRevealed] = useState(false);
  
  // Handle null/undefined
  if (value === null) {
    return <span className="text-white/30 italic">(null)</span>;
  }
  if (value === undefined) {
    return <span className="text-white/30 italic">(undefined)</span>;
  }
  if (value === '') {
    return <span className="text-white/30 italic">(empty)</span>;
  }

  const strValue = String(value);
  const isSensitive = isSensitiveField(field);
  
  // ============================================================================
  // Sensitive Field Handling
  // ============================================================================
  
  if (isSensitive && !isRevealed) {
    // Masked state - show dots with reveal button
    return (
      <div className="flex items-center gap-2">
        <span className="text-white/40 font-mono tracking-wider select-none">
          {'•'.repeat(Math.min(strValue.length, 16))}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsRevealed(true);
            // Auto-hide after 10 seconds for security
            setTimeout(() => setIsRevealed(false), 10000);
          }}
          className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors"
          title="Reveal sensitive value (auto-hides in 10s)"
        >
          <EyeIcon />
        </button>
      </div>
    );
  }
  
  if (isSensitive && isRevealed) {
    // Revealed state - show value with amber warning color and hide button
    const displayValue = strValue.length > 24 
      ? strValue.slice(0, 12) + '...' + strValue.slice(-8) 
      : strValue;
    
    return (
      <div className="flex items-center gap-2">
        <span 
          className="text-amber-400/90 font-mono text-xs break-all bg-amber-500/10 px-1.5 py-0.5 rounded"
          title={strValue}
        >
          {displayValue}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsRevealed(false);
          }}
          className="p-1 rounded hover:bg-white/10 text-amber-400/60 hover:text-amber-400 transition-colors"
          title="Hide sensitive value"
        >
          <EyeOffIcon />
        </button>
      </div>
    );
  }

  // ============================================================================
  // Normal String Handling
  // ============================================================================
  
  const needsTruncation = strValue.length > MAX_LENGTH;
  const displayValue = needsTruncation 
    ? strValue.slice(0, MAX_LENGTH - 3) + '...'
    : strValue;

  // All values are clickable to expand - never allow line breaks
  return (
    <button
      onClick={onExpand}
      className="flex items-center gap-1 text-left group max-w-full w-full"
      title={needsTruncation ? "Click to see full value" : strValue}
    >
      <span className="truncate text-white/80 group-hover:text-[#208AEC] transition-colors">
        {displayValue}
      </span>
      {needsTruncation && (
        <span className="flex-shrink-0 text-white/40 group-hover:text-[#208AEC] transition-colors">
          <ExpandIcon />
        </span>
      )}
    </button>
  );
}

// ============================================================================
// Icons
// ============================================================================

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
